import {
  Card,
  Typography,
  Grid,
  Stack,
  Avatar,
  Box,
  CircularProgress,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  LinearProgress,
  Chip,
  Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { User } from "../../../app/slices/userSlice";
import { CgProfile } from "react-icons/cg";
import { HiOutlineMail } from "react-icons/hi";
import { BiMobileAlt, BiTransferAlt } from "react-icons/bi";
import { SiOpenaccess } from "react-icons/si";
import { MdLockOutline, MdOutlineMoneyOffCsred } from "react-icons/md";
import {
  AiFillEye,
  AiFillEyeInvisible,
  AiOutlineUser,
  AiOutlineDollarCircle,
} from "react-icons/ai";
import ConfirmationDialog from "../../../helpers/ConfirmationDialog";
import { Controller, useForm } from "react-hook-form";
import { showNotification } from "../../../app/slices/notificationSlice";
import { BRAND, BRAND_GRADIENT, TONE } from "../../../components/MobileList";

const iconTileSx = {
  width: 48,
  height: 48,
  flexShrink: 0,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 24,
  color: BRAND,
  bgcolor: alpha(BRAND, 0.1),
};

// Soft decorative circle on the banner.
const blob = {
  position: "absolute",
  borderRadius: "50%",
  background: "rgba(255,255,255,0.08)",
};

const Profile = () => {
  const userInfo = useSelector((state) => state.user.userInfo);

  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openConfirm, setConfirmOpen] = useState(false);
  const [dialogData, setDialogData] = useState("");

  const passwordForm = {
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  const { control, reset, handleSubmit } = useForm({
    defaultValues: passwordForm,
  });

  const onSubmit = (data) => {
    if (data.newPassword !== data.confirmPassword)
      dispatch(
        showNotification({
          message: "كلمة المرور الجديد وتاكيدها غير متطابقتين",
          type: "error",
        }),
      );
    else {
      setDialogData(data);
      setConfirmOpen(true);
    }
  };

  const handleConfirmChangePassword = () => {
    dispatch(
      User.updatePassowrd({
        user_id: userInfo.id,
        old_password: dialogData.oldPassword,
        new_password: dialogData.confirmPassword,
      }),
    )
      .then((x) => {
        setLoading(false);
        reset({ ...passwordForm });
      })
      .catch((error) => {
        setLoading(false);
        setDialogData({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      });
    setConfirmOpen(false);
  };

  const handleCancelChangePassword = () => {
    setConfirmOpen(false);
  };

  const passwordFields = [
    {
      name: "oldPassword",
      label: "كلمة المرور القديمة",
      show: showOldPassword,
      toggle: () => setShowOldPassword(!showOldPassword),
    },
    {
      name: "newPassword",
      label: "كلمة المرور الجديدة",
      show: showNewPassword,
      toggle: () => setShowNewPassword(!showNewPassword),
    },
    {
      name: "confirmPassword",
      label: "تاكيد كلمة المرور الجديدة",
      show: showConfirmPassword,
      toggle: () => setShowConfirmPassword(!showConfirmPassword),
    },
  ];

  if (!userInfo)
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: 300 }}>
        <CircularProgress />
      </Stack>
    );

  const stats = [
    {
      label: "الرصيد",
      value: `${Number(userInfo.wallet?.balance ?? 0).toLocaleString()} IQD`,
      icon: <AiOutlineDollarCircle />,
      tone: TONE.success,
    },
    {
      label: "الديون",
      value: `${Number(userInfo.wallet?.debt ?? 0).toLocaleString()} IQD`,
      icon: <MdOutlineMoneyOffCsred />,
      tone: TONE.danger,
    },
    {
      label: "امكانية تحويل الاموال",
      value: userInfo.can_transfer ? "نعم" : "كلا",
      icon: <BiTransferAlt />,
      tone: userInfo.can_transfer ? BRAND : TONE.danger,
    },
  ];

  const details = [
    {
      label: "اسم المستخدم",
      value: userInfo.user_name,
      icon: <AiOutlineUser />,
    },
    {
      label: "البريد الالكتروني",
      value: userInfo.email,
      icon: <HiOutlineMail />,
    },
    { label: "رقم الهاتف", value: userInfo.mobile, icon: <BiMobileAlt /> },
    {
      label: "الصلاحيات",
      value: userInfo.user_roles?.[0],
      icon: <SiOpenaccess />,
    },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card
          sx={{
            position: "relative",
            overflow: "hidden",
            background: BRAND_GRADIENT,
            color: "#fff",
            p: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{ ...blob, width: 260, height: 260, top: -120, left: -60 }}
          />
          <Box
            sx={{ ...blob, width: 160, height: 160, bottom: -80, left: 220 }}
          />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2.5}
            alignItems="center"
            sx={{ position: "relative" }}
          >
            <Avatar
              src={userInfo.logo || undefined}
              sx={{
                width: 88,
                height: 88,
                borderRadius: "24px",
                bgcolor: "#fff",
                color: BRAND,
                fontSize: 36,
                fontWeight: "bold",
                boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
              }}
            >
              {userInfo.user_name?.[0]?.toUpperCase() ?? <CgProfile />}
            </Avatar>
            <Box sx={{ textAlign: { xs: "center", sm: "start" } }}>
              <Typography variant="h2" color="inherit" fontWeight="bold">
                {userInfo.user_name}
              </Typography>
              <Typography color="inherit" sx={{ opacity: 0.85, mt: 0.5 }}>
                {userInfo.email}
              </Typography>
              {userInfo.user_roles?.[0] && (
                <Chip
                  label={userInfo.user_roles[0]}
                  size="small"
                  sx={{
                    mt: 1.5,
                    color: "#fff",
                    fontWeight: 600,
                    bgcolor: "rgba(255,255,255,0.2)",
                  }}
                />
              )}
            </Box>
          </Stack>
        </Card>
      </Grid>

      {stats.map((stat) => (
        <Grid item xs={12} md={4} key={stat.label}>
          <Card sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  ...iconTileSx,
                  color: stat.tone,
                  bgcolor: alpha(stat.tone, 0.1),
                }}
              >
                {stat.icon}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {stat.label}
                </Typography>
                <Typography fontSize={18} fontWeight="bold" color="#101828">
                  {stat.value}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      ))}

      <Grid item xs={12} md={5}>
        <Card sx={{ p: 3, height: "100%" }}>
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
            المعلومات الشخصية
          </Typography>
          <Stack divider={<Divider flexItem />} spacing={1.5}>
            {details.map((row) => (
              <Stack
                key={row.label}
                direction="row"
                spacing={1.5}
                alignItems="center"
              >
                <Box
                  sx={{ ...iconTileSx, width: 38, height: 38, fontSize: 18 }}
                >
                  {row.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    {row.label}
                  </Typography>
                  <Typography fontWeight={600} color="#101828" noWrap>
                    {row.value || "-"}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Card>
      </Grid>

      <Grid item xs={12} md={7}>
        <Card sx={{ height: "100%" }}>
          {loading ? <LinearProgress /> : null}
          <Box sx={{ p: 3 }}>
            <Typography variant="h4" fontWeight="bold">
              تغيير كلمة المرور
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              استخدم كلمة مرور قوية لا تستخدمها في مكان اخر
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={2}>
                {passwordFields.map((f) => (
                  <Controller
                    key={f.name}
                    name={f.name}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type={f.show ? "text" : "password"}
                        {...field}
                        required
                        fullWidth
                        label={f.label}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MdLockOutline size={20} color={BRAND} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={f.toggle}
                                edge="end"
                              >
                                {f.show ? (
                                  <AiFillEye size={20} />
                                ) : (
                                  <AiFillEyeInvisible size={20} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                ))}
                <Box>
                  <Button
                    type="submit"
                    variant="contained"
                    disableElevation
                    size="large"
                    sx={{
                      px: 5,
                      borderRadius: 3,
                      fontWeight: "bold",
                      background: BRAND_GRADIENT,
                    }}
                  >
                    حفظ
                  </Button>
                </Box>
              </Stack>
            </form>
          </Box>
          <ConfirmationDialog
            open={openConfirm}
            title="تاكيد"
            message="هل انت متاكد من كلمة المرور !"
            confirmText="تأكيد"
            cancelText="الغاء"
            onConfirm={() => handleConfirmChangePassword()}
            onCancel={handleCancelChangePassword}
          />
        </Card>
      </Grid>
    </Grid>
  );
};

export default Profile;
